/*
 * Keeps keyboard focus out of off-screen carousel slides (WCAG 2.4.3).
 *
 * Loop mode clones whole slides, so the homepage alone carried 30 clones holding 48
 * focusable controls that a keyboard user tabbed through while they sat off-screen.
 * `inert` is deliberately not used: a clone is the visible slide half the time in loop
 * mode, and inert would swallow its clicks too.
 *
 * Visibility is measured geometrically rather than from `swiper-slide-visible`, which
 * only appears when watchSlidesProgress is on — it is off for every instance here.
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href],button,input,select,textarea,summary,iframe,[contenteditable="true"],[tabindex]';
  var STORED = 'data-a11ySwiperTabindex';

  function setReachable(slide, reachable) {
    if (reachable) {
      slide.removeAttribute('aria-hidden');
    } else {
      slide.setAttribute('aria-hidden', 'true');
    }

    var candidates = slide.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (reachable) {
        if (!el.hasAttribute(STORED)) continue;
        var previous = el.getAttribute(STORED);
        if (previous === '') {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', previous);
        }
        el.removeAttribute(STORED);
      } else {
        if (el.hasAttribute(STORED)) continue;
        // Remember what was there so an author-set tabindex survives.
        el.setAttribute(STORED, el.getAttribute('tabindex') || '');
        el.setAttribute('tabindex', '-1');
      }
    }
  }

  /*
   * A cloned slide brings copies of every id inside it, so the product gallery ended up
   * with three elements answering to Thumbnail-<section>-2 and an aria-describedby that
   * matched all of them. The clone is a visual copy: dropping its ids leaves each
   * reference resolving to the original, which is the element it described anyway.
   */
  function dedupeClones(container) {
    var clones = container.querySelectorAll('.swiper-slide-duplicate');
    for (var i = 0; i < clones.length; i++) {
      if (clones[i].id) clones[i].removeAttribute('id');
      var inner = clones[i].querySelectorAll('[id]');
      for (var j = 0; j < inner.length; j++) inner[j].removeAttribute('id');
    }
  }

  function sync(container) {
    dedupeClones(container);
    var box = container.getBoundingClientRect();
    if (!box.width) return;
    var slides = container.querySelectorAll('.swiper-slide');
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      var rect = slide.getBoundingClientRect();
      // Count a slide as reachable once most of it sits inside the viewport window.
      var overlap = Math.min(rect.right, box.right) - Math.max(rect.left, box.left);
      setReachable(slide, overlap > rect.width * 0.5);
    }
  }

  function attach(container) {
    if (container.dataset.a11ySwiperBound) return;
    var instance = container.swiper;
    if (!instance) return;
    container.dataset.a11ySwiperBound = 'true';

    var queued = false;
    var schedule = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        sync(container);
      });
    };

    // transitionEnd alone misses loop rewinds, so listen to the full set.
    ['slideChange', 'transitionEnd', 'resize', 'update', 'loopFix'].forEach(function (event) {
      instance.on(event, schedule);
    });
    schedule();
  }

  function scan() {
    var containers = document.querySelectorAll('.swiper');
    for (var i = 0; i < containers.length; i++) attach(containers[i]);
  }

  function start() {
    scan();
    // Instances are created by their own sections, some after this file runs, and
    // Shopify's theme editor re-renders sections wholesale.
    var observer = new MutationObserver(scan);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', scan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
